export type LocalizedMessage = { key: string; values?: Record<string, number | string> };

type ApiErrorLike = {
  status: number;
  code?: string;
  params?: Record<string, unknown>;
  validation?: boolean;
};

const isApiError = (error: unknown): error is ApiErrorLike => {
  if (typeof error !== 'object' || error === null) return false;
  const candidate = error as Record<string, unknown>;
  return typeof candidate.status === 'number'
    && (candidate.code === undefined || typeof candidate.code === 'string')
    && (candidate.params === undefined || (typeof candidate.params === 'object' && candidate.params !== null && !Array.isArray(candidate.params)))
    && (candidate.validation === undefined || typeof candidate.validation === 'boolean');
};

const errorKeys: Record<string, string> = {
  INVALID_CREDENTIALS: 'apiErrors.invalidCredentials',
  PASSWORD_RESET_REQUIRED: 'apiErrors.passwordResetRequired',
  EMAIL_NOT_VERIFIED: 'apiErrors.emailNotVerified',
  TERMS_REQUIRED: 'apiErrors.termsRequired',
  INVALID_BIRTH_DATE: 'apiErrors.invalidBirthDate',
  AGE_RESTRICTED: 'apiErrors.ageRestricted',
  EMAIL_ALREADY_REGISTERED: 'apiErrors.emailAlreadyRegistered',
  CURRENT_PASSWORD_INCORRECT: 'apiErrors.currentPasswordIncorrect',
  EMAIL_UNCHANGED: 'apiErrors.emailUnchanged',
  VERIFICATION_EMAIL_SEND_FAILED: 'apiErrors.verificationEmailSendFailed',
  PUBLISH_BLOCKED: 'apiErrors.publishBlocked',
  ACCOUNT_SUSPENDED: 'apiErrors.accountSuspended',
};

export function localizedApiMessage(error: unknown, fallbackKey: string): LocalizedMessage {
  if (error instanceof Error && error.name === 'SessionExpiredError') return { key: 'apiErrors.sessionExpired' };
  if (!isApiError(error)) return { key: fallbackKey };
  if (error.code === 'INSUFFICIENT_POINTS') {
    const required = Number(error.params?.required);
    const current = Number(error.params?.current);
    if (Number.isFinite(required) && required >= 0 && Number.isFinite(current) && current >= 0) {
      return { key: 'apiErrors.insufficientPoints', values: { required, current } };
    }
    return { key: 'apiErrors.insufficientPointsGeneric' };
  }
  if (error.code === 'NG_WORD_DETECTED') {
    const words = typeof error.params?.words === 'string' ? error.params.words : '';
    return words ? { key: 'apiErrors.ngWordDetected', values: { words } } : { key: 'apiErrors.ngWordDetectedGeneric' };
  }
  if (error.code && errorKeys[error.code]) return { key: errorKeys[error.code] };
  if (error.status === 0) return { key: 'apiErrors.network' };
  if (error.validation) return { key: 'apiErrors.validation' };
  return { key: fallbackKey };
}
